import React from 'react';
import PropTypes from 'prop-types';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import GaugePieChart from './GaugePieChart';
import { getGaugePieFields } from '../../utils/gaugeDataFormatUtils';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';

// eslint-disable-next-line complexity
function getMetricSource({ metricRequests, metrics }) {
    if (
        metricRequests.length !== 1 ||
        (metricRequests[0].groupBy.length === 0 &&
            metricRequests[0].metrics.length === 0)
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([metricSource])) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

// eslint-disable-next-line complexity
export default function GaugePieChartWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    function buildContextMenuData(eventData, groupBy) {
        return {
            [groupBy]: {
                name: eventData[groupBy],
                values: metricRequest.metrics.reduce(
                    (result, metric) => ({
                        ...result,
                        [metric]: eventData[metric],
                    }),
                    {}
                ),
            },
        };
    }

    function onContextMenuOpen(eventData, currentTarget) {
        props.onContextMenuOpen({
            data: buildContextMenuData(eventData, metricRequest.groupBy[0]),
            currentTarget,
            metricRequest,
        });
    }

    const gaugeFields = getGaugePieFields(
        metricRequest,
        metricSource,
        props.numberDecimalFormatter
    );

    return (
        <GaugePieChart
            actions={props.actions}
            dataPoints={gaugeFields.formattedData}
            chartProperties={props.configuration}
            legendFormatter={props.legendFormatter}
            metricName={gaugeFields.nameKey}
            metricValueKey={gaugeFields.valueKey}
            onContextMenuOpen={onContextMenuOpen}
        />
    );
}

GaugePieChartWrapper.propTypes = {
    actions: PropTypes.instanceOf(Array),
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    legendFormatter: PropTypes.func.isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    onContextMenuOpen: PropTypes.func.isRequired,
};
